import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  Gamepad2,
  ClipboardList,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
} from 'lucide-react';

const EMPTY_FORM = { slug: '', name: '', publisher: '', version: '', description: '', is_active: true };

export default function AdminGameSystemsPage() {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = create
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSystems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/game-systems', { params: { include_inactive: true } });
      setSystems(res.data);
    } catch (err) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchSystems(); }, [fetchSystems]);

  const openCreate = () => {
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (system) => {
    setEditTarget(system);
    setFormData({
      slug: system.slug,
      name: system.name,
      publisher: system.publisher || '',
      version: system.version || '',
      description: system.description || '',
      is_active: system.is_active,
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.slug.trim() || !formData.name.trim()) {
      toast.error(t('error_required', language));
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await axios.patch(`/api/game-systems/${editTarget.id}`, formData);
      } else {
        await axios.post('/api/game-systems', formData);
      }
      toast.success(t('success_saved', language));
      setFormOpen(false);
      fetchSystems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (system) => {
    try {
      await axios.patch(`/api/game-systems/${system.id}`, { is_active: !system.is_active });
      toast.success(t('success_saved', language));
      fetchSystems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/game-systems/${deleteDialog.id}`);
      toast.success(t('success_deleted', language));
      setDeleteDialog(null);
      fetchSystems();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-cinzel font-bold flex items-center gap-2">
              <Gamepad2 className="h-6 w-6" />
              {t('admin_game_systems', language)}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('admin_game_systems_desc', language)}
            </p>
          </div>
          <Button onClick={openCreate} className="fantasy-button">
            <Plus className="mr-2 h-4 w-4" />
            {t('admin_game_system_create', language)}
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : systems.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <Gamepad2 className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {t('admin_game_system_none', language)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {systems.map(system => (
              <Card key={system.id} className={`glass-card ${!system.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <Gamepad2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-cinzel font-semibold">{system.name}</span>
                        {system.version && (
                          <span className="text-xs text-muted-foreground">v{system.version}</span>
                        )}
                        {system.is_builtin && (
                          <Badge variant="outline" className="text-xs">
                            {t('admin_game_system_builtin', language)}
                          </Badge>
                        )}
                        {!system.is_active && (
                          <Badge variant="destructive" className="text-xs">
                            {t('admin_game_system_disabled', language)}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                        <code className="bg-muted px-1 rounded">{system.slug}</code>
                        {system.publisher && <span>{system.publisher}</span>}
                        {system.description && (
                          <span className="truncate max-w-xs">{system.description}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/admin/game-systems/${system.id}/templates`)}
                    >
                      <ClipboardList className="h-4 w-4 mr-1" />
                      {t('admin_system_templates', language)}
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(system)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('edit', language)}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(system)}>
                          {system.is_active
                            ? <><ToggleLeft className="mr-2 h-4 w-4" />{t('admin_game_system_archive', language)}</>
                            : <><ToggleRight className="mr-2 h-4 w-4" />{t('admin_game_system_reenable', language)}</>
                          }
                        </DropdownMenuItem>
                        {!system.is_builtin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => setDeleteDialog(system)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('admin_game_system_delete', language)}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="font-cinzel">
                {editTarget ? t('edit', language) : t('admin_game_system_create', language)}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="gs-slug">{t('admin_game_system_slug', language)} *</Label>
                <Input
                  id="gs-slug"
                  placeholder="dnd5e"
                  value={formData.slug}
                  onChange={e => setFormData(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                  disabled={!!editTarget}
                  required
                />
                {!editTarget && (
                  <p className="text-xs text-muted-foreground">
                    {t('admin_game_system_slug_help', language)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="gs-name">{t('admin_game_system_name', language)} *</Label>
                <Input
                  id="gs-name"
                  placeholder="D&D 5e"
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="gs-publisher">{t('admin_game_system_publisher', language)}</Label>
                  <Input
                    id="gs-publisher"
                    placeholder="Wizards of the Coast"
                    value={formData.publisher}
                    onChange={e => setFormData(p => ({ ...p, publisher: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gs-version">{t('admin_game_system_version', language)}</Label>
                  <Input
                    id="gs-version"
                    placeholder="2024"
                    value={formData.version}
                    onChange={e => setFormData(p => ({ ...p, version: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="gs-desc">{t('admin_game_system_description', language)}</Label>
                <Input
                  id="gs-desc"
                  placeholder={t('admin_placeholder_short_desc', language)}
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>{t('admin_game_system_active', language)}</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                {t('cancel', language)}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('save', language)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={open => !open && setDeleteDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-destructive">
              {t('admin_game_system_delete', language)}
            </DialogTitle>
            <DialogDescription>
              {deleteDialog?.is_builtin
                ? t('admin_game_system_no_delete_builtin', language)
                : t('admin_game_system_confirm_delete', language)}
              {deleteDialog && !deleteDialog.is_builtin && (
                <span className="block mt-2 font-semibold">{deleteDialog.name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              {t('cancel', language)}
            </Button>
            {!deleteDialog?.is_builtin && (
              <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('delete', language)}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
